/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.base.analytics

/**
 * Analytics event builders for the onboarding feature.
 *
 * Returns [AnalyticsEvent] instances with onboarding-specific event types and parameters.
 * Event type and parameter key strings are intentionally kept local to this helper since
 * they are not general-purpose and do not belong in [Types] or [ParamKeys].
 */
object OnboardingEvents {
    private object Type {
        const val PAIN_SELECTED = "onboarding_pain_selected"
        const val FIRST_PATIENT_ADDED = "onboarding_first_patient_added"
        const val FIRST_PATIENT_SKIPPED = "onboarding_first_patient_skipped"
        const val FIRST_SESSION_LOGGED = "onboarding_first_session_logged"
        const val FIRST_SESSION_SKIPPED = "onboarding_first_session_skipped"
        const val COMPLETED = "onboarding_completed"
    }

    fun painSelected(pains: List<String>, screenTimeSeconds: Int): AnalyticsEvent =
        AnalyticsEvent(Type.PAIN_SELECTED).withParams(
            "pains" to pains.joinToString(","),
            "screen_time_seconds" to screenTimeSeconds.toString(),
        )

    fun firstPatientAdded(
        hasEmail: Boolean,
        feeModifiedFromDefault: Boolean,
        screenTimeSeconds: Int,
    ): AnalyticsEvent =
        AnalyticsEvent(Type.FIRST_PATIENT_ADDED).withParams(
            "has_email" to hasEmail.toString(),
            "fee_modified" to feeModifiedFromDefault.toString(),
            "screen_time_seconds" to screenTimeSeconds.toString(),
        )

    fun firstPatientSkipped(): AnalyticsEvent =
        AnalyticsEvent(Type.FIRST_PATIENT_SKIPPED)

    fun firstSessionLogged(paid: Boolean, screenTimeSeconds: Int): AnalyticsEvent =
        AnalyticsEvent(Type.FIRST_SESSION_LOGGED).withParams(
            "paid" to paid.toString(),
            "screen_time_seconds" to screenTimeSeconds.toString(),
        )

    fun firstSessionSkipped(): AnalyticsEvent =
        AnalyticsEvent(Type.FIRST_SESSION_SKIPPED)

    fun completed(durationSeconds: Int): AnalyticsEvent =
        AnalyticsEvent(Type.COMPLETED).withParam("duration_seconds", durationSeconds.toString())
}
